import { Box, IconButton, Stack, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import {
  formatConditionSummary,
  RowenaConditionalAttrs,
  RowenaConditionalOperator,
  RowenaConditionalValueKind,
} from './rowenaConditional';
import { RowenaAvailableVariable } from 'types/api/SystemTenant/RowenaAI/RowenaContentTypes';

const VALID_BORDER = '#3B4797';
const INVALID_BORDER = '#D32F2F';
const VALID_PILL_BG = '#3B4797';
const INVALID_PILL_BG = '#D32F2F';

export default function ConditionalBlockView({ node, editor, getPos }: NodeViewProps) {
  const attrs = node.attrs as RowenaConditionalAttrs;
  const variables = (editor.storage as Record<string, { variables?: Record<string, RowenaAvailableVariable> }>)
    .rowenaConditional?.variables;

  const variableKnown = !attrs.variable || !!variables?.[attrs.variable];
  const borderColor = variableKnown ? VALID_BORDER : INVALID_BORDER;
  const pillBg = variableKnown ? VALID_PILL_BG : INVALID_PILL_BG;
  const summary = formatConditionSummary(attrs);

  const handleEditClick = () => {
    const onEdit = (
      editor.storage as Record<
        string,
        { onEdit?: (pos: number, current: RowenaConditionalAttrs) => void }
      >
    ).rowenaConditional?.onEdit;
    if (!onEdit) return;
    const pos = typeof getPos === 'function' ? getPos() : 0;
    onEdit(pos, {
      variable: attrs.variable ?? '',
      operator: (attrs.operator as RowenaConditionalOperator) ?? 'eq',
      value: attrs.value ?? '',
      valueKind: (attrs.valueKind as RowenaConditionalValueKind) ?? 'literal',
    });
  };

  return (
    <NodeViewWrapper
      as='div'
      className='rowena-conditional'
      style={{
        border: `1px dashed ${borderColor}`,
        borderRadius: 6,
        padding: '6px 10px 8px',
        margin: '6px 0',
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        contentEditable={false}
        sx={{ userSelect: 'none', mb: 0.75 }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            bgcolor: pillBg,
            color: '#fff',
            fontSize: 11,
            fontFamily: 'monospace',
            fontWeight: 600,
            px: 1,
            py: 0.25,
            borderRadius: 1,
          }}
          title={!variableKnown ? `Variable @${attrs.variable} is not available for the selected page type` : undefined}
        >
          <Typography component='span' variant='caption' sx={{ color: '#fff', fontFamily: 'inherit', fontSize: 11 }}>
            if {summary}
          </Typography>
        </Box>
        <IconButton
          size='small'
          onClick={handleEditClick}
          sx={{ p: 0.25 }}
          title='Edit condition'
        >
          <EditOutlinedIcon fontSize='small' />
        </IconButton>
      </Stack>
      <NodeViewContent as='div' />
    </NodeViewWrapper>
  );
}
